import _ from 'classnames';
import React, { useEffect, useState } from 'react';
import { SliderHandles } from "./components/SliderHandles.js";
import { SliderNotch } from "./components/SliderNotch.js";
import { SliderNotchLabels } from "./components/SliderNotchLabel.js";
import { SliderRange } from "./components/SliderRange.js";
import { SliderTrack } from "./components/SliderTrack.js";
import { SliderValueLabel } from "./components/SliderValueLabel.js";
import { TrackLabel } from "./components/TrackLabel.js";
import { SliderProvider } from "./providers/SliderProvider.js";
import { useEventCallback } from "../../helper/hooks/useEventCallback.js";
import styles from "./Slider.module.js";

/**
 * @deprecated use {@link SliderProps}
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Slider({
  values,
  onChange,
  className,
  ...props
}) {
  // Manually map props for backward compatibility
  // 1) Map deprecated props
  const stepSize = ('steps' in props ? props.steps : 'stepSize' in props ? props.stepSize : undefined) ?? 1;
  const minValue = calcStep('min' in props ? props.min : props.minValue, stepSize);
  const maxValue = calcStep('max' in props ? props.max : props.maxValue, stepSize);
  const showValueLabel = 'withLabel' in props && props.withLabel ? 'labelAsCurrency' in props && props.labelAsCurrency ? 'currency' : 'number' : 'showValueLabel' in props ? props.showValueLabel : undefined;

  // 2) Read parameters and assign default values. Can be replaced by destructuring when deprecated
  //    props are removed.
  const minValueLabel = 'minValueLabel' in props ? props.minValueLabel : undefined;
  const maxValueLabel = 'minValueLabel' in props ? props.maxValueLabel : undefined;
  const showMinValue = 'showMinValue' in props ? props.showMinValue : undefined;
  const showMaxValue = 'showMaxValue' in props ? props.showMaxValue : undefined;
  const notches = 'notches' in props ? props.notches : undefined;
  const snapNotch = 'snapNotch' in props && props.snapNotch;
  return /*#__PURE__*/_jsx(SliderRoot, {
    values: values,
    onChange: onChange,
    stepSize: stepSize,
    minValue: minValue,
    maxValue: maxValue,
    showValueLabel: showValueLabel,
    minValueLabel: minValueLabel,
    maxValueLabel: maxValueLabel,
    showMinValue: showMinValue,
    showMaxValue: showMaxValue,
    notches: notches,
    snapNotch: snapNotch,
    className: className
  });
}
function SliderRoot({
  values,
  onChange,
  stepSize = 1,
  minValue,
  maxValue,
  showValueLabel,
  minValueLabel,
  maxValueLabel,
  showMinValue,
  showMaxValue,
  notches,
  snapNotch,
  className
}) {
  // Glossary:
  // ========
  //  - value    An unprocessed input value.
  //  - stop     A value validated and assigned to a position on the slider track (the later handle).
  //  - maxRange The distance (not pixel) between the min and max value on the slide track.
  //  - handle   The visualization of a stop.
  //  - track    The area where the stops are placed.
  //  - range    The distance between the lowest and the highest stop. For one stop, the distance
  //             between the minValue and the stop.
  //
  //  |<--------------------- maxRange ----------------->|
  //  |                   ,- handle at the second stop   |
  //  | ====( )==========( )====...====( )=======( )==== |  := track
  //  |      `- stopMin                           `- stopMax
  //  |      |<-------------- range ------------->|      |
  //  X<- minValue                            maxValue ->X
  //

  // +===============================================================================+
  // | Fun fact: No useMemo on purpose because computation is 2.5x faster (on a M1). |
  // |           Between two renders the input values should have changed anyway.    |
  // +===============================================================================+

  // the overall distance between the min and max value of the slider.
  const maxRange = maxValue - minValue;

  // clamp all values to the min and max boundaries of the slider to compute the stops.
  const stops = values.map(value => calcStep(clampValue(value, minValue, maxValue), stepSize));
  const [stopMin, stopMax] = [Math.min(...stops), Math.max(...stops)];
  const handleValueChange = useEventCallback((index, value, snapValue) => {
    const oldStop = stops[index];
    if (oldStop !== undefined) {
      const lowerStop = stops[index - 1];
      const upperStop = stops[index + 1];
      const newSnap = snapNotch && snapValue ? calcSnapNotch(value, notches, 10) : value;
      const newStop = calcStep(clampValue(newSnap, lowerStop ?? minValue, upperStop ?? maxValue), stepSize, 0.25);
      if (newStop !== null) {
        const newStops = [...stops];
        newStops[index] = newStop;
        onChange?.(newStops);
      }
    }
  }, [maxValue, minValue, onChange, stepSize, stops]);

  // the current active handle
  const [activeHandle, setActiveHandle] = useState(undefined);
  // the z-index of each handle ordered by their last usage
  const [elevations, setElevations] = useState([]);
  // calculate the z-index ordering of all handles
  useEffect(() => {
    if (activeHandle !== undefined) {
      setElevations(elevation => {
        const prev = elevation[activeHandle] ?? activeHandle;
        elevation[activeHandle] = values.length;
        return elevation.map(curr => {
          if (curr && curr >= prev) {
            return curr - 1;
          }
          return curr;
        });
      });
    }
  }, [activeHandle, values.length]);
  const [trackEle, setTrackEle] = useState(null);
  return /*#__PURE__*/_jsx(SliderProvider, {
    minValue: minValue,
    maxRange: maxRange,
    trackEle: trackEle,
    children: /*#__PURE__*/_jsxs("div", {
      className: _(styles.wrapper, className),
      children: [minValueLabel && /*#__PURE__*/_jsx(TrackLabel, {
        content: minValueLabel,
        vAlign: "start",
        hAlign: "start"
      }), maxValueLabel && /*#__PURE__*/_jsx(TrackLabel, {
        content: maxValueLabel,
        vAlign: "start",
        hAlign: "end"
      }), showMinValue && /*#__PURE__*/_jsx(TrackLabel, {
        vAlign: "end",
        hAlign: "start",
        content: showMinValue(minValue)
      }), showMaxValue && /*#__PURE__*/_jsx(TrackLabel, {
        vAlign: "end",
        hAlign: "end",
        content: showMaxValue(maxValue)
      }), /*#__PURE__*/_jsx(SliderTrack, {
        trackRef: setTrackEle
      }), /*#__PURE__*/_jsx(SliderRange, {
        stops: stops,
        stopMin: stopMin,
        stopMax: stopMax,
        maxRange: maxRange,
        minValue: minValue
      }), notches?.map(({
        value
      }, index) =>
      /*#__PURE__*/
      /* eslint-disable react/no-array-index-key */
      _jsx(SliderNotch, {
        value: value
      }, index)
      /* eslint-enable react/no-array-index-key */), notches && /*#__PURE__*/_jsx(SliderNotchLabels, {
        notches: notches
      }), /*#__PURE__*/_jsx(SliderHandles, {
        stops: stops,
        maxRange: maxRange,
        minValue: minValue,
        elevations: elevations,
        onHandleActive: setActiveHandle,
        onHandleMoved: handleValueChange,
        trackEle: trackEle
      }), showValueLabel && stops.map((stop, index) =>
      /*#__PURE__*/
      /* eslint-disable react/no-array-index-key */
      _jsx(SliderValueLabel, {
        index: index,
        value: stop,
        maxRange: maxRange,
        elevation: elevations[index],
        format: showValueLabel,
        onValueChange: handleValueChange,
        onLabelActive: setActiveHandle
      }, index)
      /* eslint-enable react/no-array-index-key */)]
    })
  });
}

/**
 * Maps an input value the matching step value.
 * @param value The input value.
 * @param stepSize The step size to apply.
 */

/**
 * Maps an input value the matching step value.
 * @param value The input value.
 * @param stepSize The step size to apply.
 * @param threshold A threshold value between ']0, 1]' describing when an approached step value
 *                  should be matched:
 *                  1 == The previous step is immediately matched.
 *                  0 >  The percentage the input value must approach the previous step to be
 *                       matched, e.g. 25% == 0.25 := step + stepSize * 0.25 <= value
 */

function calcStep(value, stepSize, threshold = 1) {
  if (stepSize === 1) {
    return Math.trunc(value); // Shortcut if stepSize is 1, same as `value - value % stepSize`
  }
  const rest = value % stepSize;
  if (rest <= stepSize * threshold) {
    return value - rest;
  }
  return null;
}
const calcSnapNotch = (value, notches, around) => {
  if (notches?.length) {
    for (const {
      value: notchValue
    } of notches) {
      if (value < notchValue + around && value > notchValue - around) {
        return notchValue;
      }
    }
  }
  return value;
};
const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);